import { get } from "@rails/request.js";

type MultiSelectConfig = {
  multiSelectUrl: string;
  multiSelectQueryParam: string;
  multiSelectMinLength: string;
  multiSelectMaxSelections: string;
  multiSelectInputName: string;
};

type MultiSelectItem = {
  value: string;
  label: string;
};

const optionSelector = "[role='option']:not([aria-disabled])";

export default (defaultValues: MultiSelectItem[]) => ({
  activeOption: null,
  selectedItems: [],
  query: "",
  config: {},
  results: {
    content: "",
    visible: false,
    isLoading: false,
  },

  /**
   * Initialize the multi-select component.
   */
  init() {
    this.selectedItems = defaultValues;

    this.config = this.$el.dataset as MultiSelectConfig;
    this.$anchor(
      this.$refs.inputContainer,
      this.$refs.resultsContainer,
      null,
      null,
      true
    );

    this.$watch("results.visible", () => {
      if (!this.results.visible) {
        this.activeOption = null;
        this.results.content = "";
      }

      this.$dispatch("multi-select:toggle", {
        action: this.results.visible ? "open" : "close",
        inputName: this.config.multiSelectInputName,
      });
    });
  },

  get showClear(): boolean {
    return this.query.length > 0 || this.selectedItems.length > 0;
  },

  /**
   * Determines if an item in the search result list should be highlighted with
   * an 'active' CSS class.
   *
   * @param resultItem - The search result item element.
   * @returns - True if the ID of the active option in the search result list
   * matches the ID of the result item passed in.
   */
  isActive(resultItem: Element) {
    return this.activeOption?.id === resultItem.id;
  },

  /**
   * Determines if an item in the search result list should be highlighted with
   * a 'selected' CSS class.
   *
   * @param resultItem - The search result item element.
   * @returns - True if the search result item has already been selected.
   */
  isSelected(resultItem: Element) {
    const existingItemIndex = this.selectedItems.findIndex((item) => {
      return (
        item.value === resultItem.getAttribute("data-listbox-option-value")
      );
    });

    return existingItemIndex > -1;
  },

  /**
   * Fetches new search results if the search query has become more specific, or
   * resets the search results entirely if the search query is less than the
   * minimum length required to perform a search. If a max selected attribute is
   * provided, an additional check is performed to ensure the total amount of
   * selected values is less than the maximum allowable selected values.
   */
  onInputChange() {
    this.query = this.$refs.searchInput.value;

    const query = this.query.trim();

    if (query && query.length >= this.config.multiSelectMinLength) {
      if (
        this.config.multiSelectMaxSelections == null ||
        this.selectedItems.length < this.config.multiSelectMaxSelections
      ) {
        this.fetchResults(query);
      } else {
        return;
      }
    } else {
      this.resetResults();
      if (query.length === 0 && this.selectedItems.length === 0) {
        this.$dispatch("multi-select:clear", {
          inputName: this.config.multiSelectInputName,
        });
      }
    }
  },

  /**
   * Selects an item from the search results list when clicked on.
   *
   * @param event - The click event.
   * @returns - Nothing if the target event isn't an instance of aneElement.
   */
  onResultsClick(event: any) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const selected = event.target.closest(optionSelector);

    if (selected) {
      this.commit(event.target);
    }
  },

  onArrowKeydown() {
    if (this.results.visible) {
      this.selectSibling(true);
    } else {
      this.fetchResults(this.$refs.searchInput.value);
    }
  },

  /**
   * Selects the current active item from the search results list on enter
   * keypresses.
   */
  onEnterKeydown() {
    if (this.activeOption && this.results.visible) {
      this.commit(this.activeOption);
    }
  },

  /**
   * Removes the last item in the list of selected items on backspace
   * keypresses, essentially treating each selected item as an individual
   * character in the search query input field. Dispatches a
   * `multi-select:remove` event for the removed item.
   */
  onBackspaceKeydown() {
    if (this.$refs.searchInput.value === "" && this.selectedItems.length > 0) {
      const removedItem = this.selectedItems.pop();

      this.dispatchRemove(removedItem);
    }
    this.enableSelections();
  },

  /**
   * Dispatches a `multi-select:remove` event for a removed item. Single source
   * of truth for the removal eventing shared by the backspace and chip-removal
   * paths.
   *
   * @param removedItem - The item removed from the list of selected items.
   */
  dispatchRemove(removedItem: MultiSelectItem) {
    this.$dispatch("multi-select:remove", {
      value: removedItem.value,
      label: removedItem.label,
      inputName: this.config.multiSelectInputName,
    });
  },

  /**
   * Clears all selected items, effectively setting the multi-select back to its
   * initial state.
   */
  clear() {
    // Clear and shift focus to the search input field
    this.$refs.searchInput.value = "";
    this.query = "";
    this.$refs.searchInput.focus();

    // Reset the dropdown button to be enabled
    this.enableSelections();

    // Clear the selected items
    this.selectedItems = [];

    // Reset the search results list
    this.resetResults();

    // Dispatch the clear event
    this.$dispatch("multi-select:clear", {
      inputName: this.config.multiSelectInputName,
    });
  },

  /**
   * Clears all selected items and the search query input field, effectively
   * setting the multi-select back to its initial state. Works similar to the
   * clear method above but for externally-triggered resets such as a reset
   * button on wrapping form, which doesn't require focus to be returned to the
   * search input, or the search results list to be hidden.
   */
  reset() {
    this.$refs.searchInput.value = "";
    this.query = "";
    this.selectedItems = [];
  },

  /**
   * Hides the dropdown on clicks outside the component, discarding any search
   * query that was never committed to a selection.
   */
  onClickOutside() {
    this.$refs.searchInput.value = "";
    this.query = "";
    this.resetResults();
  },

  /**
   * Sets the active element from the list of search results based on up/down
   * arrow keyboard navigation within the list of search results.
   *
   * @param next - A boolean determining if the next sibling should be selected.
   */
  selectSibling(next: boolean) {
    const options = this.options;
    const selected = this.activeOption;
    const index = options.indexOf(selected);
    const sibling = next ? options[index + 1] : options[index - 1];
    const defaultSibling = next ? options[0] : options[options.length - 1];

    this.activeOption = sibling || defaultSibling;
    if (this.activeOption) this.activeOption.scrollIntoView(false);
  },

  /**
   * Persists the active search result item and marks it as selected,
   * subsequently adding the search result item to the list of selected items.
   *
   * @param searchResultItemElement - The selected search result item.
   * @return - If the selected item is already in the list of selected items,
   * the selected item will instead be removed.
   */
  commit(searchResultItemElement: Element) {
    const { listboxOptionValue, listboxOptionLabel } = (
      searchResultItemElement as any
    ).dataset;

    const existingItemIndex = this.selectedItems.findIndex((item) => {
      return item.value === listboxOptionValue;
    });
    const isRemoved = existingItemIndex > -1;

    if (isRemoved) {
      this.selectedItems.splice(existingItemIndex, 1);
    } else {
      this.selectedItems = this.selectedItems.concat([
        {
          value: listboxOptionValue,
          label: listboxOptionLabel,
        },
      ]);
    }

    // If a max selection limit is set, reset the search results list
    // This prevents the user from selecting more items than the limit allows
    if (
      this.config.multiSelectMaxSelections != null &&
      this.selectedItems.length >= this.config.multiSelectMaxSelections
    ) {
      this.resetResults();
      this.disableSelections();
    }

    this.$refs.searchInput.focus();

    // Reset the search query input field
    this.$refs.searchInput.value = "";
    this.query = "";

    this.$dispatch(`multi-select:${isRemoved ? "remove" : "add"}`, {
      value: listboxOptionValue,
      label: listboxOptionLabel,
      item: searchResultItemElement,
      inputName: this.config.multiSelectInputName,
    });
  },

  /**
   * Removes an item from the list of selected items, based on a provided
   * element from the list of selected item elements. Deleted items from the
   * comma-separated list of selected items are then sanitized out to prevent
   * empty selections.
   *
   * @param selectedItem
   */
  removeSelectedItem(selectedItem: MultiSelectItem) {
    const existingItemIndex = this.selectedItems.findIndex((item) => {
      return item.value === selectedItem?.value;
    });

    if (existingItemIndex > -1) {
      this.selectedItems.splice(existingItemIndex, 1);

      const nextSelectedItemChip = this.$refs.selectedItems?.querySelector(
        ".clio-ui-multi-select-chip-label"
      );

      // Focus on the next selected item chip element, if available
      if (this.selectedItems.length > 0) {
        nextSelectedItemChip.focus();
      } else {
        this.$refs.searchInput.focus();
      }

      this.enableSelections();

      this.dispatchRemove(selectedItem);
    }
  },

  /**
   * Shifts focus to the previous chip in the list of selected item chips.
   * Technically we are focusing on the label portion of the chip, not the
   * chip container itself.
   *
   * @param event - The keyboard event.
   * @param currentIndex - The index of the current focused chip.
   */
  focusPreviousChip(event: KeyboardEvent, currentIndex: number) {
    const chipLabels = this.$refs.selectedItems?.querySelectorAll(
      ".clio-ui-multi-select-chip-label"
    );

    if (!chipLabels) return;

    const previousIndex = currentIndex - 1;
    const targetIndex =
      previousIndex >= 0 ? previousIndex : chipLabels.length - 1;

    this.focusChip(targetIndex);
  },

  /**
   * Shifts focus to the next chip in the list of selected item chips.
   * Technically we are focusing on the label portion of the chip, not the
   * chip container itself.
   *
   * @param event - The keyboard event.
   * @param currentIndex - The index of the current focused chip.
   */
  focusNextChip(event: KeyboardEvent, currentIndex: number) {
    const chipLabels = this.$refs.selectedItems?.querySelectorAll(
      ".clio-ui-multi-select-chip-label"
    );

    const nextIndex = currentIndex + 1;
    const targetIndex = nextIndex < chipLabels.length ? nextIndex : 0;

    this.focusChip(targetIndex);
  },

  /**
   * Sets tabindex="-1" on all chips except the target one and shifts focus to
   * it. Technically we are focusing on the label portion of the chip, not the
   * chip container itself.
   *
   * @param index - The index of the chip to shift focus to.
   */
  focusChip(index: number) {
    const chipLabels = this.$refs.selectedItems?.querySelectorAll(
      ".clio-ui-multi-select-chip-label"
    );

    if (!chipLabels) return;

    // Set all chips to tabindex="-1" except the target one
    chipLabels.forEach((chip, i) => {
      (chip as HTMLElement).setAttribute("tabindex", i === index ? "0" : "-1");
    });

    (chipLabels[index] as HTMLElement).focus();
  },

  /**
   * Toggles the visibility of the search results list.
   */
  toggleResults() {
    if (!this.results.visible) {
      this.fetchResults(this.$refs.searchInput.value);
    } else {
      this.resetResults();
    }
  },

  /**
   * Fetch the search results lists based on the provided query.
   *
   * @param query - The search query entered by the user.
   */
  async fetchResults(query: string) {
    this.results.isLoading = true;

    try {
      this.$dispatch("multi-select:loadstart", {
        inputName: this.config.multiSelectInputName,
      });
      const queryParamObj = {};
      queryParamObj[this.config.multiSelectQueryParam] = query;
      const response = await get(this.config.multiSelectUrl, {
        query: queryParamObj,
      });

      if (response.ok) {
        this.replaceResults(await response.html);
        this.$dispatch("multi-select:load", {
          inputName: this.config.multiSelectInputName,
        });
        this.$dispatch("multi-select:loadend", {
          inputName: this.config.multiSelectInputName,
        });
      } else {
        const responseError =
          (response && response.statusText) || response.status;
        throw Error(responseError);
      }

      this.results.isLoading = false;
    } catch (error) {
      this.$dispatch("multi-select:error", {
        message: error.message,
        inputName: this.config.multiSelectInputName,
      });
      this.$dispatch("multi-select:loadend", {
        inputName: this.config.multiSelectInputName,
      });
      this.results.isLoading = false;
    }
  },

  /**
   * Renders the list of search results in the form of a menu component returned
   * from the server. If a menu is already present, it is replaced. The first
   * search result menu item is highlighted as active by default.
   *
   * @param html - The HTML containing the menu of search results.
   */
  replaceResults(html: string) {
    this.results.content = html;

    this.$nextTick(() => {
      this.results.visible = true;
      this.selectSibling(true);
    });
  },

  /**
   * Reset the search results list by hiding the list.
   */
  resetResults() {
    this.results.visible = false;
  },

  disableSelections() {
    this.$refs.dropdownButton.setAttribute("aria-disabled", "true");
    this.$refs.dropdownButton.setAttribute("disabled", "true");
  },

  enableSelections() {
    this.$refs.dropdownButton.removeAttribute("aria-disabled");
    this.$refs.dropdownButton.removeAttribute("disabled");
  },

  /**
   * Return a list of all items in the search result list that are not disabled.
   */
  get options(): Element[] {
    return Array.from(
      this.$refs.resultsContainer.querySelectorAll(optionSelector)
    );
  },
});
